import dynamic from 'next/dynamic';
import { ChevronDownIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import { formatDate, formatJobContent } from 'common/utils/career';
import { JobType } from 'service/careers';

const IsNewFlag = dynamic(() => import('./is-new-flag'), { ssr: false });
interface JobContentPropsType {
  job: JobType;
  expandedJob: string | null;
  onToggleExpansion: (jobId: string) => void;
}

const JobContent = ({
  job,
  expandedJob,
  onToggleExpansion,
}: JobContentPropsType) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="mb-2 text-xl font-bold leading-normal text-pp-text-primary">
            {job.title}
            <IsNewFlag date={job.modified} />
          </h3>

          <div className="mb-4 flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <svg
                className="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {job.location?.join(' / ') || 'Tokyo, Japan'}
            </div>
            <div className="flex items-center gap-1">
              <svg
                className="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatDate(job.modified)}
            </div>
            {job.contract_type?.length > 0 && (
              <div className="flex items-center gap-1">
                <svg
                  className="size-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {job.contract_type?.join(' / ')}
              </div>
            )}
          </div>

          {expandedJob === job.id ? (
            <div
              className="border-pp-line-primary prose prose-sm max-w-none border-t pt-4 text-[15px]"
              dangerouslySetInnerHTML={{
                __html: formatJobContent({
                  content: job.content,
                  isPreview: false,
                }),
              }}
            />
          ) : (
            <div
              className="mb-4 line-clamp-5 text-[15px] text-pp-text-secondary"
              dangerouslySetInnerHTML={{
                __html: formatJobContent({
                  content: job.content_preview,
                  isPreview: true,
                }),
              }}
            />
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={() => onToggleExpansion(job.id)}
              className="flex items-center gap-1 text-sm font-medium text-growth-green hover:opacity-80"
            >
              {expandedJob === job.id ? 'Hide Details' : 'View Details'}
              <ChevronDownIcon
                className={`size-4 transition-transform ${
                  expandedJob === job.id ? 'rotate-180' : ''
                }`}
              />
            </button>

            <a
              href="https://forms.gle/KLLicf3iiLYaF7n76"
              rel="noopener noreferrer"
              target="_blank"
              className="inline-flex items-center gap-1 rounded-full bg-growth-green px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
            >
              Apply Now
              <ExternalLinkIcon className="size-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobContent;
